import { useEffect, useRef } from "react";
import { ToolCallEntry } from "../ChatBox/types";
import { refetchTemplateFields } from "./refetchTemplateFields";

/**
 * Agent tools that write diligence field values.
 *
 * Draft writes are deliberately NOT staged as a mutation plan — a draft is
 * unpublished and reversible, so an approval gate would review the same thing
 * twice. That means there is no plan-approval moment to hang a refresh off (see
 * MutationPlanCardConnected, which refetches after an approved plan executes),
 * so the open Diligence form has to learn about these writes from the chat
 * stream instead.
 */
export const FIELD_WRITING_TOOL_NAMES: readonly string[] = [
  "saveDiligenceFieldDrafts",
];

/**
 * Loose shape of one chat history entry.
 *
 * Tools hang off `data`, not the entry itself: ChatMessage is
 * `{ type, data, id }` and it is the inner Message that is a
 * MarkdownWithToolsMessage (`tools`) or ToolsReactMessage (`allTools`) — see
 * streamEventToHistory, which reads `message.data.tools`. Reading
 * `message.tools` silently matches nothing.
 */
interface MaybeToolsMessage {
  data?: {
    tools?: ToolCallEntry[];
    allTools?: ToolCallEntry[];
  };
}

/**
 * Ids of field-writing tool calls in `history` that have completed successfully
 * and are not in `seen`.
 *
 * Keyed on the call id so a refetch fires once per write rather than on every
 * re-render of the same history, and so a streaming turn that appends more
 * messages does not re-trigger earlier writes.
 */
export function newFieldWriteCallIds(
  history: readonly unknown[],
  seen: ReadonlySet<string>
): string[] {
  const ids: string[] = [];
  for (const message of history) {
    const data = (message as MaybeToolsMessage)?.data;
    const entries = [...(data?.tools ?? []), ...(data?.allTools ?? [])];
    for (const entry of entries) {
      if (!entry?.toolName) continue;
      if (!FIELD_WRITING_TOOL_NAMES.includes(entry.toolName)) continue;
      // Wait for the call to come back, and ignore a failed one — there is
      // nothing new to read if the write was rejected.
      if (!entry.results || entry.results.error) continue;
      const id = entry.toolCallId ?? entry.toolId;
      if (!id || seen.has(id) || ids.includes(id)) continue;
      ids.push(id);
    }
  }
  return ids;
}

/**
 * Refresh the open Diligence template as soon as the agent writes a field,
 * rather than on the template store's 60s poll — which is what made an
 * agent-written draft look like it had not happened.
 *
 * Fires on the tool RESULT, mid-turn, so the form updates while the agent is
 * still writing its reply instead of after the turn ends.
 */
export function useRefetchTemplateFieldsOnFieldWrite(
  history: readonly unknown[]
): void {
  const seen = useRef<Set<string>>(new Set());

  useEffect(() => {
    const fresh = newFieldWriteCallIds(history, seen.current);
    if (!fresh.length) return;
    for (const id of fresh) seen.current.add(id);
    refetchTemplateFields();
  }, [history]);
}
